import {FileIcon} from '@primer/octicons-react'
import {Checkbox, TreeView, VisuallyHidden} from '@primer/react'
import {type SyntheticEvent, memo, useCallback, useEffect, useId, useRef, useState, useSyncExternalStore} from 'react'

import styles from './FileTreeSelect.module.css'

export interface FileTreeSelectNode {
  id: string
  name: string
  kind: 'file' | 'directory'
  children?: FileTreeSelectNode[]
}

export type FileTreeSelectNodeState = 'checked' | 'indeterminate' | 'unchecked'

export interface FileTreeSelectProps {
  /** The top-level nodes to render. Directories carry their children inline (the full tree is expected to be loaded already; this component does not fetch). */
  nodes: FileTreeSelectNode[]
  /** Returns the tri-state selection for a node id. The caller owns selection semantics; this component only renders the given state and reports toggles via `onToggle`. */
  getNodeState: (id: string) => FileTreeSelectNodeState
  /** Subscribes a listener that fires whenever any node's selection state may have changed, so rows can independently re-render. Returns an unsubscribe function. */
  subscribe: (listener: () => void) => () => void
  /** Called when a row is activated (click, Enter, or Space). `kind` mirrors the node's own `kind`. */
  onToggle: (id: string, kind: 'file' | 'directory') => void
  /** How many siblings to render per "Show more" batch. Defaults to 300. */
  batchSize?: number
  /** Accessible label for the tree. Defaults to "Files". */
  ariaLabel?: string
}

const DEFAULT_BATCH_SIZE = 300
const DEFAULT_ARIA_LABEL = 'Files'

function useNodeState(
  subscribe: (listener: () => void) => () => void,
  getNodeState: (id: string) => FileTreeSelectNodeState,
  id: string,
) {
  const getSnapshot = () => getNodeState(id)
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

function useVisibleCount(items: FileTreeSelectNode[], batch: number, instanceId: string) {
  const total = items.length
  const [visibleCount, setVisibleCount] = useState(batch)
  const [prevBatch, setPrevBatch] = useState(batch)
  const pendingFocusIdRef = useRef<string | null>(null)

  if (batch !== prevBatch) {
    setPrevBatch(batch)
    setVisibleCount(batch)
  }

  useEffect(() => {
    const id = pendingFocusIdRef.current
    if (!id) return
    pendingFocusIdRef.current = null
    document.getElementById(id)?.focus()
  }, [visibleCount])

  const showMore = useCallback(() => {
    const next = Math.min(visibleCount + batch, total)
    // Focus the first newly-revealed row once the "Show more" control unmounts, so keyboard focus
    // isn't stranded when the final batch removes it from the tree.
    if (next >= total) {
      const nextItem = items[visibleCount]
      pendingFocusIdRef.current = nextItem ? toDomId(instanceId, nextItem.id) : null
    }
    setVisibleCount(next)
  }, [visibleCount, batch, total, items, instanceId])

  return {visibleCount: Math.min(visibleCount, total), showMore}
}

// Namespaces a node's DOM id by the owning FileTreeSelect instance, so two instances rendering the
// same path never collide and focus restoration can't resolve to a row in a different tree.
function toDomId(instanceId: string, nodeId: string): string {
  return `${instanceId}:${nodeId}`
}

export function FileTreeSelect({
  nodes,
  getNodeState,
  subscribe,
  onToggle,
  batchSize = DEFAULT_BATCH_SIZE,
  ariaLabel = DEFAULT_ARIA_LABEL,
}: FileTreeSelectProps) {
  const instanceId = useId()
  const normalizedBatchSize = Number.isFinite(batchSize) && batchSize >= 1 ? Math.floor(batchSize) : DEFAULT_BATCH_SIZE
  const {visibleCount, showMore} = useVisibleCount(nodes, normalizedBatchSize, instanceId)
  const visibleNodes = nodes.slice(0, visibleCount)
  const remaining = nodes.length - visibleCount

  return (
    <TreeView aria-label={ariaLabel} truncate={false}>
      {visibleNodes.map(node => (
        <FileTreeSelectRow
          key={node.id}
          node={node}
          getNodeState={getNodeState}
          subscribe={subscribe}
          onToggle={onToggle}
          batchSize={normalizedBatchSize}
          instanceId={instanceId}
        />
      ))}
      {remaining > 0 && (
        <ShowMoreRow
          key="__file-tree-select-show-more__"
          count={Math.min(normalizedBatchSize, remaining)}
          onShowMore={showMore}
        />
      )}
    </TreeView>
  )
}

const ShowMoreRow = memo(function ShowMoreRowImpl({
  count,
  onShowMore,
  context,
}: {
  count: number
  onShowMore: () => void
  /** Full path of the containing directory when this row is inside an expanded SubTree, so sibling "Show more" rows sharing a basename get distinct accessible names. Omitted at root level. */
  context?: string
}) {
  const id = useId()
  const handleSelect = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault()
      onShowMore()
    },
    [onShowMore],
  )
  const label = `Show ${count} more`
  return (
    <TreeView.Item id={id} onSelect={handleSelect} aria-label={context ? `${label} in ${context}` : undefined}>
      <span className={styles.TreeItemOverflow}>{label}</span>
    </TreeView.Item>
  )
})

const FileTreeSelectRow = memo(function FileTreeSelectRowImpl({
  node,
  getNodeState,
  subscribe,
  onToggle,
  batchSize,
  instanceId,
}: {
  node: FileTreeSelectNode
  getNodeState: (id: string) => FileTreeSelectNodeState
  subscribe: (listener: () => void) => () => void
  onToggle: (id: string, kind: 'file' | 'directory') => void
  batchSize: number
  instanceId: string
}) {
  const state = useNodeState(subscribe, getNodeState, node.id)
  const isDirectory = node.kind === 'directory'
  const [isExpanded, setIsExpanded] = useState(false)
  const children = node.children ?? []
  const {visibleCount, showMore} = useVisibleCount(children, batchSize, instanceId)
  const domId = toDomId(instanceId, node.id)

  const handleSelect = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault()
      onToggle(node.id, node.kind)
    },
    [onToggle, node.id, node.kind],
  )

  const label = (
    <span className={styles.TreeItemLabel} data-testid={`${node.id}-tree-item`}>
      <Checkbox
        className={styles.TreeItemCheckbox}
        checked={state === 'checked'}
        indeterminate={state === 'indeterminate'}
        readOnly
        tabIndex={-1}
        aria-hidden
      />
      {node.name}
      {state === 'checked' && <VisuallyHidden> selected</VisuallyHidden>}
      {state === 'indeterminate' && <VisuallyHidden> partially selected</VisuallyHidden>}
    </span>
  )

  if (!isDirectory) {
    return (
      <TreeView.Item id={domId} onSelect={handleSelect} containIntrinsicSize="auto 2rem">
        <TreeView.LeadingVisual>
          <FileIcon />
        </TreeView.LeadingVisual>
        {label}
      </TreeView.Item>
    )
  }

  const visibleChildren = isExpanded ? children.slice(0, visibleCount) : []
  const remaining = isExpanded ? children.length - visibleCount : 0

  return (
    <TreeView.Item
      id={domId}
      onSelect={handleSelect}
      expanded={isExpanded}
      onExpandedChange={setIsExpanded}
      containIntrinsicSize="auto 2rem"
    >
      <TreeView.LeadingVisual>
        <TreeView.DirectoryIcon />
      </TreeView.LeadingVisual>
      {label}
      <TreeView.SubTree>
        {isExpanded &&
          visibleChildren.map(child => (
            <FileTreeSelectRow
              key={child.id}
              node={child}
              getNodeState={getNodeState}
              subscribe={subscribe}
              onToggle={onToggle}
              batchSize={batchSize}
              instanceId={instanceId}
            />
          ))}
        {remaining > 0 && (
          <ShowMoreRow
            key="__file-tree-select-show-more__"
            count={Math.min(batchSize, remaining)}
            onShowMore={showMore}
            context={node.id}
          />
        )}
      </TreeView.SubTree>
    </TreeView.Item>
  )
})
